import { useQuery, useMutation, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  inputsApi,
  type CreateInputRequest,
  type UpdateInputRequest,
} from '../services/inputsApi';

export const useInputsPaged = (pageNumber: number, pageSize: number, search?: string) => {
  return useQuery({
    queryKey: ['inputs-paged', pageNumber, pageSize, search],
    queryFn: () => inputsApi.getInputsPaged(pageNumber, pageSize, search),
    placeholderData: keepPreviousData,
  });
};

export const useReceiptInputs = (supplierId: string, search: string) =>
  useInfiniteQuery({
    queryKey: ['receipt-inputs', supplierId, search],
    enabled: !!supplierId,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => inputsApi.getInputsPaged(pageParam, 100, search || undefined, supplierId),
    getNextPageParam: (lastPage) => lastPage.currentPage < lastPage.totalPages
      ? lastPage.currentPage + 1
      : undefined,
  });

export const useInputLookup = (open: boolean, search: string, supplierId?: string) =>
  useInfiniteQuery({
    queryKey: ['inputs', 'lookup', search, supplierId],
    enabled: open,
    initialPageParam: 1,
    // The repository caps pages at 100; search and paginate instead of searching one catalog slice.
    queryFn: ({ pageParam }) => inputsApi.getInputsPaged(pageParam, 100, search || undefined, supplierId),
    getNextPageParam: (lastPage) => lastPage.currentPage < lastPage.totalPages
      ? lastPage.currentPage + 1
      : undefined,
  });

export const useInputById = (id: string) => {
  return useQuery({
    queryKey: ['inputs', id],
    queryFn: () => inputsApi.getInputById(id),
  });
};

export const useLowStockInputs = () => {
  return useQuery({
    queryKey: ['inputs', 'low-stock'],
    queryFn: inputsApi.getLowStockInputs,
  });
};

export const useCreateInput = () => {
  return useMutation({
    mutationFn: (payload: CreateInputRequest) =>
      inputsApi.createInput(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-inputs'] });
      queryClient.invalidateQueries({ queryKey: ['inputs', 'low-stock'] });
    },
  });
};

export const useUpdateInput = (id: string) => {
  return useMutation({
    mutationFn: (payload: UpdateInputRequest) =>
      inputsApi.updateInput(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-inputs'] });
      queryClient.invalidateQueries({ queryKey: ['inputs', id] });
      queryClient.invalidateQueries({ queryKey: ['inputs', 'low-stock'] });
    },
  });
};

export const useDeleteInput = () => {
  return useMutation({
    mutationFn: (id: string) => inputsApi.deleteInput(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inputs'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-inputs'] });
      queryClient.invalidateQueries({ queryKey: ['inputs', 'low-stock'] });
    },
  });
};
